import type { Agegroup } from "~~/shared/ts/interfaces/Agegroup";
import type { Department } from "~~/shared/ts/interfaces/Department";
import type { Document } from "~~/shared/ts/interfaces/Document";
import type { FAQuestion } from "~~/shared/ts/interfaces/FAQuestion";
import type { Gallery } from "~~/shared/ts/interfaces/Gallery";
import type NavAgegroups from "~~/shared/ts/interfaces/NavAgegroups";
import type { StrapiData, StrapiDataCollection } from "~~/shared/ts/interfaces/strapi/StrapiData";

export class ApiUtil {

    static async getNavAgeGroups(populate = false): Promise<StrapiData<NavAgegroups>> {
        const { public: { apiUrl } } = useRuntimeConfig();
        const { data } = await useFetch(`${apiUrl}/api/agegroupnav`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            params: {
                populate: `agegroups`,
            },
        });
        return data.value as StrapiData<NavAgegroups>;
    }

    static async getAgeGroups(populate = false): Promise<StrapiDataCollection<Agegroup>> {
        const { public: { apiUrl } } = useRuntimeConfig();
        const { data } = await useFetch(`${apiUrl}/api/agegroups`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            params: {
                populate: `${populate ? '*' : ''}`,
            },
        });
        return data.value as StrapiDataCollection<Agegroup>;
    }

    static async getAgeGroup(documentId: string, populate = false): Promise<Agegroup> {
        const { public: { apiUrl } } = useRuntimeConfig();
        const { data } = await useFetch(`${apiUrl}/api/agegroups/${documentId}?populate[leaders][populate]=image&populate[activities][populate]=*&populate[group_photo][populate]=*&populate[quarterly_plans][populate]=*&[populate][registration_form][populate]=*&populate[Contact][populate]=*`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        const response = data.value as StrapiData<Agegroup>;
        if (!response || !response.data) {
            throw new Error(`Agegroup with documentId ${documentId} not found`);
        }
        return response.data;
    }

    static async getDocuments(populate = false): Promise<Document[]> {
        const { public: { apiUrl } } = useRuntimeConfig();
        const { data } = await useFetch(`${apiUrl}/api/documents`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            params: {
                populate: `${populate ? '*' : ''}`,
            },
        });
        return data.value as Document[];
    }

    static async getGalleries(populate = false): Promise<Gallery[]> {
        const { public: { apiUrl } } = useRuntimeConfig();
        const { data } = await useFetch(`${apiUrl}/api/galleries`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            params: {
                populate: `${populate ? '*' : ''}`,
            },
        });
        return data.value as Gallery[];
    }

    static async getGallery(documentId: string, populate = false): Promise<Gallery> {
        const { public: { apiUrl } } = useRuntimeConfig();
        const { data } = await useFetch(`${apiUrl}/api/galleries/${documentId}?populate[0]=images&populate[1]=activity`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });
        const response = data.value as StrapiData<Gallery>;
        if (!response || !response.data) {
            throw new Error(`Gallery with documentId ${documentId} not found`);
        }
        return response.data; 
    }

    static async getFaq(populate = false): Promise<StrapiDataCollection<FAQuestion>> {
        const { public: { apiUrl } } = useRuntimeConfig();
        const { data } = await useFetch(`${apiUrl}/api/faqs`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            params: {
                populate: `${populate ? '*' : ''}`,
            },
        });
        return data.value as StrapiDataCollection<FAQuestion>;
    }

    static async getDepartments(populate = true): Promise<StrapiDataCollection<Department>> {
        const { public: { apiUrl } } = useRuntimeConfig();
        const { data } = await useFetch(`${apiUrl}/api/departments`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            params: {
                // populate: `${populate ? '*' : ''}`,
                'populate[leaders][populate]': '*',
                'populate[Contact]': '*',
            },
        });
        return data.value as StrapiDataCollection<Department>;
    }
}